import React, { useEffect, useState } from "react";
import { ContainerPanel } from "@components/organisms/container-panel/container-panel";
import { DropAreaDeactivationOverlayContainer } from "@components/templates/layouts/drop-area-deactivation-overlay-container/drop-area-deactivation-overlay-container";
import { SubHeader } from "@components/atoms/sub-header/sub-header";
import { SubTitle } from "@components/atoms/sub-title/sub-title";
import { ContainerPane } from "@components/organisms/container-pane/container-pane";
import { TransferButtonNewStructure } from "@src/components/molecules/transfer-button-new-structure/transfer-button-new-structure";
import TreeViewInitialStructure from "../tree-structure/tree-components/tree-view-initial-structure";
import { InitialStructurePanelControlBar } from "@src/components/molecules/initial-structure-panel-control-bar/initial-structure-panel-control-bar";
import { calcContentPaneHeight } from "@src/cloud-connect/utils/calculate-content-pane-height";
import { useI18N } from "@src/language/language-context";
import { hasInitialStructureLoadCompleted } from "@src/cloud-connect/node-manager/load-our-node-structures";
import { getInitialStructureNodeset } from "@src/cloud-connect/dependency-injection-container/get-initial-structure-nodeset";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import styles from "./initial-structure-panel.module.scss";
import { FlexColumnContainer } from "@src/components/templates/layouts/flex-column-container/flex-column-container";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { querySourceByIdUseCase } from "@src/cloud-connect/client-configuration/use-cases/query-source-by-id-use-case";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import SpinnerWithVariant from "@src/components/atoms/SpinnerWithVariant/spinner-with-variant";

const cssClassName = "initial-structure-panel";

export const InitialStructurePanel = () => {
  const i18n = useI18N();
  const [isLoaded, setIsLoaded] = useState(hasInitialStructureLoadCompleted);
  const [isFlatMapEmpty, setIsFlatMapEmpty] = useState(true);
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");
  const texts = {
    subtitle: i18n("modules.initialStructurePanel.subtitle"),
    lastReceivedAt: i18n("modules.initialStructurePanel.lastReceivedAt"),
    errorFetchingLastUpdatedAt: i18n(
      "modules.initialStructurePanel.errorFetchingLastUpdatedAt",
    ),
    noTimestampAvailable: i18n(
      "modules.initialStructurePanel.noTimestampAvailable",
    ),
  };
  const [loadingLastUpdatedAt, setLoadingLastUpdatedAt] = useState(false);

  const initialStructureNodeset = getInitialStructureNodeset();
  const sourceId = useSelector(
    (state: RootState) => state.yourSlice.connectionView.source.source_id,
  );

  useEffect(() => {
    const updateFlatMap = () => {
      const newFlatMap = initialStructureNodeset.getFlatMap();

      setIsFlatMapEmpty(!newFlatMap || newFlatMap.size === 0);
      setIsLoaded(true);
    };

    updateFlatMap();

    initialStructureNodeset.addObserver(updateFlatMap);

    return () => {
      initialStructureNodeset.removeObserver(updateFlatMap);
    };
  }, [initialStructureNodeset]);

  useEffect(() => {
    if (!sourceId) return;

    const controller = new AbortController();
    const signal = controller.signal;

    async function getLastUpdatedAt() {
      setLastUpdatedAt("");
      setLoadingLastUpdatedAt(true);
      try {
        const response = await querySourceByIdUseCase(sourceId, false, signal);
        if (signal.aborted) return;

        if (response.success && response.data) {
          setLastUpdatedAt(response?.data?.nodeset_last_updated_at || "");
        } else {
          setLastUpdatedAt("");
          errorSnackbar(texts.errorFetchingLastUpdatedAt, 5000);
        }
      } catch (error) {
        if (signal.aborted) return;
        console.error("Error fetching last updated at:", error);
      } finally {
        setLoadingLastUpdatedAt(false);
      }
    }

    getLastUpdatedAt();

    return () => {
      controller.abort();
    };
  }, [sourceId]);

  return (
    <ContainerPanel>
      <DropAreaDeactivationOverlayContainer>
        <SubHeader>
          <FlexColumnContainer verticalGap={2}>
            <SubTitle text={texts.subtitle} />
            <p className={styles[`${cssClassName}__last-updated-at`]}>
              {texts.lastReceivedAt}
              {loadingLastUpdatedAt ? (
                <span className={styles[`${cssClassName}__spinner-wrapper`]}>
                  <SpinnerWithVariant variant="extra-small" />
                </span>
              ) : lastUpdatedAt ? (
                <TimezoneDate date={lastUpdatedAt} />
              ) : (
                texts.noTimestampAvailable
              )}
            </p>
          </FlexColumnContainer>
          <TransferButtonNewStructure />
        </SubHeader>
        <ContainerPane>
          {isLoaded && !isFlatMapEmpty && <InitialStructurePanelControlBar />}
          <div
            style={{
              height: calcContentPaneHeight(
                isLoaded && !isFlatMapEmpty ? 397 : 341,
              ),
              overflowY: "scroll",
              overflowX: "hidden",
            }}
          >
            <TreeViewInitialStructure />
          </div>
        </ContainerPane>
      </DropAreaDeactivationOverlayContainer>
    </ContainerPanel>
  );
};
